import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrmOrgStore } from '../store/crmOrg.store';
import organizationsService from '../services/crm/organizationsService';
import { supportService, SupportKbArticle } from '../services/supportService';
import { updateMetaTags, DEFAULT_OG_IMAGE } from '../utils/seo';
import { crmGettingStartedArticle } from '../content/help/crmGettingStarted';
import { crmOverviewArticle } from '../content/help/crmOverview';
import { crmContactsArticle } from '../content/help/crmContacts';
import { crmCompaniesArticle } from '../content/help/crmCompanies';
import { crmDealsArticle } from '../content/help/crmDeals';
import { crmActivitiesArticle } from '../content/help/crmActivities';
import { crmTelemarketingArticle } from '../content/help/crmTelemarketing';
import { crmSettingsArticle } from '../content/help/crmSettings';
import { consultantGettingStartedArticle } from '../content/help/consultantGettingStarted';
import { consultantChatArticle } from '../content/help/consultantChat';
import { consultantKnowledgeArticle } from '../content/help/consultantKnowledge';
import { consultantHistoryArticle } from '../content/help/consultantHistory';
import { consultantSettingsArticle } from '../content/help/consultantSettings';
import { ideaGettingStartedArticle } from '../content/help/ideaGettingStarted';
import { ideaWizardArticle } from '../content/help/ideaWizard';
import { ideaSavingArticle } from '../content/help/ideaSaving';
import { businessPlanGettingStartedArticle } from '../content/help/businessPlanGettingStarted';
import { businessPlanEditingArticle } from '../content/help/businessPlanEditing';
import { businessPlanExportsArticle } from '../content/help/businessPlanExports';
import { investorCrmGettingStartedArticle } from '../content/help/investorCrmGettingStarted';
import { investorCrmInvestorsArticle } from '../content/help/investorCrmInvestors';
import { investorCrmPipelineArticle } from '../content/help/investorCrmPipeline';
import { brandKitGettingStartedArticle } from '../content/help/brandKitGettingStarted';
import { linkedInGettingStartedArticle } from '../content/help/linkedInGettingStarted';
import { pressReleaseGettingStartedArticle } from '../content/help/pressReleaseGettingStarted';
import { socialMediaGettingStartedArticle } from '../content/help/socialMediaGettingStarted';
import { socialMediaViewsArticle } from '../content/help/socialMediaViews';
import { coldEmailGettingStartedArticle } from '../content/help/coldEmailGettingStarted';
import { coldEmailNavigationArticle } from '../content/help/coldEmailNavigation';
import { affiliateGettingStartedArticle } from '../content/help/affiliateGettingStarted';
import { linkIntelligenceGettingStartedArticle } from '../content/help/linkIntelligenceGettingStarted';
import { financeGettingStartedArticle } from '../content/help/financeGettingStarted';
import { pricingGettingStartedArticle } from '../content/help/pricingGettingStarted';
import { revenueOperationsGettingStartedArticle } from '../content/help/revenueOperationsGettingStarted';
import { supplierOperationsGettingStartedArticle } from '../content/help/supplierOperationsGettingStarted';
import { riskManagementGettingStartedArticle } from '../content/help/riskManagementGettingStarted';
import { projectManagementGettingStartedArticle } from '../content/help/projectManagementGettingStarted';
import { legalHrGettingStartedArticle } from '../content/help/legalHrGettingStarted';
import { seoContentGettingStartedArticle } from '../content/help/seoContentGettingStarted';
import { seoToolsGettingStartedArticle } from '../content/help/seoToolsGettingStarted';
import { ecommerceGettingStartedArticle } from '../content/help/ecommerceGettingStarted';
import { staffManagementGettingStartedArticle } from '../content/help/staffManagementGettingStarted';
import { recruitmentGettingStartedArticle } from '../content/help/recruitmentGettingStarted';
import { brandKitTabsArticle } from '../content/help/brandKitTabs';
import { linkedInProfilesArticle } from '../content/help/linkedInProfiles';
import { pressReleaseQuotesArticle } from '../content/help/pressReleaseQuotes';
import { affiliateProgramSetupArticle } from '../content/help/affiliateProgramSetup';
import { linkIntelligenceQrArticle } from '../content/help/linkIntelligenceQr';
import { socialMediaComposerArticle } from '../content/help/socialMediaComposer';
import { coldEmailCampaignsArticle } from '../content/help/coldEmailCampaigns';
import { coldEmailContactsArticle } from '../content/help/coldEmailContacts';
import { financeTabsArticle } from '../content/help/financeTabs';
import { pricingTabsArticle } from '../content/help/pricingTabs';
import { revenueOperationsTransactionsArticle } from '../content/help/revenueOperationsTransactions';
import { revenueOperationsInsightsArticle } from '../content/help/revenueOperationsInsights';
import { supplierOperationsNavigationArticle } from '../content/help/supplierOperationsNavigation';
import { supplierOperationsQuotesInventoryArticle } from '../content/help/supplierOperationsQuotesInventory';
import { riskManagementProfilesArticle } from '../content/help/riskManagementProfiles';
import { riskManagementFiltersArticle } from '../content/help/riskManagementFilters';
import { projectManagementTabsArticle } from '../content/help/projectManagementTabs';
import { projectManagementTimelineArticle } from '../content/help/projectManagementTimeline';
import { legalHrToolsArticle } from '../content/help/legalHrTools';
import { legalHrDocumentsArticle } from '../content/help/legalHrDocuments';
import { staffManagementTabsArticle } from '../content/help/staffManagementTabs';
import { staffManagementTimeLeaveArticle } from '../content/help/staffManagementTimeLeave';
import { recruitmentTabsArticle } from '../content/help/recruitmentTabs';
import { recruitmentJobsPipelineArticle } from '../content/help/recruitmentJobsPipeline';
import { seoContentTabsArticle } from '../content/help/seoContentTabs';
import { seoToolsTabsArticle } from '../content/help/seoToolsTabs';
import { seoToolsIntegrationsArticle } from '../content/help/seoToolsIntegrations';
import { ecommerceTabsArticle } from '../content/help/ecommerceTabs';
import { ecommerceBulkImportArticle } from '../content/help/ecommerceBulkImport';

type ArticlePreview = SupportKbArticle & { content?: string };

const HelpCenter: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams();
  const { activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped, setCurrentUserId } = useCrmOrgStore();
  const [searchParams] = useSearchParams();
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articles, setArticles] = useState<ArticlePreview[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGroupId, setActiveGroupId] = useState('all');
  const articleSlug = searchParams.get('article');
  const isPublicView = !user?.id;

  useEffect(() => {
    setCurrentUserId(user?.id ?? null);
  }, [user?.id, setCurrentUserId]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!user?.id || isBootstrapped) {
        setLoadingOrg(false);
        return;
      }

      try {
        const response = await organizationsService.list();
        const list = response.data || [];
        if (list.length === 0) {
          const created = await organizationsService.create('My Organization');
          if (created.data?.id) {
            setActiveOrgId(created.data.id);
          }
        } else if (!activeOrgId) {
          setActiveOrgId(list[0]?.id ?? null);
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organizations:', err);
        setError(err?.message || 'Failed to load organization');
      } finally {
        setBootstrapped(true);
        setLoadingOrg(false);
      }
    };

    void bootstrap();
  }, [user?.id, activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped]);

  const seedArticles = useMemo(
    () => [
      crmGettingStartedArticle,
      crmOverviewArticle,
      crmContactsArticle,
      crmCompaniesArticle,
      crmDealsArticle,
      crmActivitiesArticle,
      crmTelemarketingArticle,
      crmSettingsArticle,
      consultantGettingStartedArticle,
      consultantChatArticle,
      consultantKnowledgeArticle,
      consultantHistoryArticle,
      consultantSettingsArticle,
      ideaGettingStartedArticle,
      ideaWizardArticle,
      ideaSavingArticle,
      businessPlanGettingStartedArticle,
      businessPlanEditingArticle,
      businessPlanExportsArticle,
      investorCrmGettingStartedArticle,
      investorCrmInvestorsArticle,
      investorCrmPipelineArticle,
      brandKitGettingStartedArticle,
      linkedInGettingStartedArticle,
      pressReleaseGettingStartedArticle,
      socialMediaGettingStartedArticle,
      socialMediaViewsArticle,
      coldEmailGettingStartedArticle,
      coldEmailNavigationArticle,
      affiliateGettingStartedArticle,
      linkIntelligenceGettingStartedArticle,
      financeGettingStartedArticle,
      pricingGettingStartedArticle,
      revenueOperationsGettingStartedArticle,
      supplierOperationsGettingStartedArticle,
      riskManagementGettingStartedArticle,
      projectManagementGettingStartedArticle,
      legalHrGettingStartedArticle,
      seoContentGettingStartedArticle,
      seoToolsGettingStartedArticle,
      ecommerceGettingStartedArticle,
      staffManagementGettingStartedArticle,
      recruitmentGettingStartedArticle,
      brandKitTabsArticle,
      linkedInProfilesArticle,
      pressReleaseQuotesArticle,
      affiliateProgramSetupArticle,
      linkIntelligenceQrArticle,
      socialMediaComposerArticle,
      coldEmailCampaignsArticle,
      coldEmailContactsArticle,
      financeTabsArticle,
      pricingTabsArticle,
      revenueOperationsTransactionsArticle,
      revenueOperationsInsightsArticle,
      supplierOperationsNavigationArticle,
      supplierOperationsQuotesInventoryArticle,
      riskManagementProfilesArticle,
      riskManagementFiltersArticle,
      projectManagementTabsArticle,
      projectManagementTimelineArticle,
      legalHrToolsArticle,
      legalHrDocumentsArticle,
      staffManagementTabsArticle,
      staffManagementTimeLeaveArticle,
      recruitmentTabsArticle,
      recruitmentJobsPipelineArticle,
      seoContentTabsArticle,
      seoToolsTabsArticle,
      seoToolsIntegrationsArticle,
      ecommerceTabsArticle,
      ecommerceBulkImportArticle,
    ],
    []
  );

  const publicArticles = useMemo(
    () =>
      seedArticles.map((article) => ({
        ...article,
        id: article.slug,
        organization_id: 'public',
        is_published: true,
      })),
    [seedArticles]
  );

  const publishedArticles = useMemo(() => articles.filter((a) => a.is_published), [articles]);

  const moduleGroups = useMemo(
    () => [
      { id: 'consultant', title: 'AI Business Consultant', slugPrefixes: ['consultant-'] },
      { id: 'business-plan', title: 'AI Business Plan Generator', slugPrefixes: ['business-plan-'] },
      { id: 'brand-kit', title: 'Brand Kit Creator', slugPrefixes: ['brand-kit-'] },
      { id: 'crm', title: 'AI Customer CRM', slugPrefixes: ['crm-'] },
      { id: 'linkedin', title: 'AI LinkedIn Enhancer', slugPrefixes: ['linkedin-'] },
      { id: 'press-release', title: 'AI Press Release Generator', slugPrefixes: ['press-release-'] },
      { id: 'social-media', title: 'AI Social Media Planner', slugPrefixes: ['social-media-'] },
      { id: 'cold-email', title: 'AI Cold Email Engine', slugPrefixes: ['cold-email-'] },
      { id: 'affiliate', title: 'AI Affiliate Marketing Engine', slugPrefixes: ['affiliate-marketing-'] },
      { id: 'link-intelligence', title: 'AI Link Intelligence Suite', slugPrefixes: ['link-intelligence-'] },
      { id: 'finance', title: 'AI Finance Command Center', slugPrefixes: ['financial-planning-', 'finance-'] },
      { id: 'pricing', title: 'AI Pricing Intelligence Suite', slugPrefixes: ['pricing-intelligence-', 'pricing-'] },
      { id: 'revenue-ops', title: 'AI Revenue Operations Hub', slugPrefixes: ['revenue-operations-'] },
      { id: 'supplier-ops', title: 'AI Supplier Operations Hub', slugPrefixes: ['supplier-operations-'] },
      { id: 'risk', title: 'AI Risk Management Command Center', slugPrefixes: ['risk-management-'] },
      { id: 'investor-crm', title: 'AI Investor CRM & Fundraising Hub', slugPrefixes: ['investor-crm-'] },
      { id: 'legal-hr', title: 'AI Legal & HR Document Hub', slugPrefixes: ['legal-hr-'] },
      { id: 'project-mgmt', title: 'AI Project Management Hub', slugPrefixes: ['project-management-'] },
      { id: 'staff-mgmt', title: 'AI Staff Management & HR Hub', slugPrefixes: ['staff-management-'] },
      { id: 'recruitment', title: 'AI Recruitment & ATS Platform', slugPrefixes: ['recruitment-'] },
      { id: 'seo-content', title: 'SEO Content Hub', slugPrefixes: ['seo-content-'] },
      { id: 'seo-tools', title: 'SEO Tools Hub', slugPrefixes: ['seo-tools-'] },
      { id: 'ecommerce', title: 'Ecommerce Hub', slugPrefixes: ['ecommerce-'] },
      { id: 'idea-generator', title: 'Business Idea Generator', slugPrefixes: ['idea-generator-'] },
    ],
    []
  );

  const groupedArticles = useMemo(() => {
    const groups = moduleGroups.map((group) => ({ ...group, articles: [] as ArticlePreview[] }));
    const otherGroup = { id: 'other', title: 'Other', slugPrefixes: [], articles: [] as ArticlePreview[] };

    publishedArticles.forEach((article) => {
      const match = groups.find((group) => group.slugPrefixes.some((prefix) => article.slug?.startsWith(prefix)));
      if (match) {
        match.articles.push(article);
      } else {
        otherGroup.articles.push(article);
      }
    });

    if (otherGroup.articles.length > 0) groups.push(otherGroup);
    return groups;
  }, [moduleGroups, publishedArticles]);

  const filteredGroups = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return groupedArticles
      .filter((group) => activeGroupId === 'all' || group.id === activeGroupId)
      .map((group) => {
        if (!normalizedQuery) return group;
        const groupMatches = group.title.toLowerCase().includes(normalizedQuery);
        const articles = group.articles.filter((article) => {
          const titleMatch = article.title?.toLowerCase().includes(normalizedQuery);
          const slugMatch = article.slug?.toLowerCase().includes(normalizedQuery);
          return titleMatch || slugMatch;
        });
        return {
          ...group,
          articles: groupMatches ? group.articles : articles,
        };
      })
      .filter((group) => {
        if (!normalizedQuery) return true;
        return group.articles.length > 0 || group.title.toLowerCase().includes(normalizedQuery);
      });
  }, [groupedArticles, activeGroupId, searchQuery]);

  const loadArticles = async () => {
    if (isPublicView) {
      setArticles(publicArticles);
      const slugToSelect = routeSlug || articleSlug;
      if (slugToSelect) {
        const match = publicArticles.find((item) => item.slug === slugToSelect);
        if (match?.id) {
          setSelectedId(match.id);
          return;
        }
      }
      if (!selectedId && publicArticles.length > 0) {
        setSelectedId(publicArticles[0].id);
      }
      return;
    }
    if (!activeOrgId) return;
    setLoadingArticles(true);
    setError(null);
    try {
      const list = await supportService.listKbArticles({ organization_id: activeOrgId });

      const existingBySlug = new Map(list.map((article) => [article.slug, article]));
      const articlesToCreate = seedArticles.filter((article) => !existingBySlug.has(article.slug));
      const articlesToPublish = seedArticles.filter((article) => {
        const existing = existingBySlug.get(article.slug);
        return existing && !existing.is_published;
      });
      const selectArticleId = (nextList: SupportKbArticle[]) => {
        const slugToSelect = routeSlug || articleSlug;
        if (slugToSelect) {
          const match = nextList.find((item) => item.slug === slugToSelect);
          if (match?.id) {
            setSelectedId(match.id);
            return true;
          }
        }
        if (!selectedId) {
          const first = nextList.find((item) => item.is_published);
          if (first?.id) {
            setSelectedId(first.id);
            return true;
          }
        }
        return false;
      };

      if (articlesToCreate.length > 0 || articlesToPublish.length > 0) {
        for (const article of articlesToCreate) {
          try {
            await supportService.createKbArticle({
              organization_id: activeOrgId,
              title: article.title,
              slug: article.slug,
              content: article.content,
              is_published: true,
            });
          } catch (createError) {
            console.error(`Failed to seed KB article ${article.slug}`, createError);
          }
        }
        for (const article of articlesToPublish) {
          const existing = existingBySlug.get(article.slug);
          if (!existing?.id) continue;
          try {
            await supportService.updateKbArticle(existing.id, {
              organization_id: activeOrgId,
              title: article.title,
              content: article.content,
              is_published: true,
            });
          } catch (updateError) {
            console.error(`Failed to publish KB article ${article.slug}`, updateError);
          }
        }
        const refreshed = await supportService.listKbArticles({ organization_id: activeOrgId });
        setArticles(refreshed);
        if (!selectArticleId(refreshed)) {
          const created = refreshed.find((a) => a.slug === articlesToCreate[0]?.slug);
          if (created?.id) setSelectedId(created.id);
        }
        return;
      }

      setArticles(list);
      selectArticleId(list);
    } catch (err: any) {
      console.error('Failed to load help articles:', err);
      setError(err?.message || 'Failed to load help articles');
    } finally {
      setLoadingArticles(false);
    }
  };

  useEffect(() => {
    if (isPublicView) {
      void loadArticles();
      return;
    }
    if (activeOrgId) {
      void loadArticles();
    }
  }, [activeOrgId, isPublicView]);

  const selectedArticle = useMemo(
    () => articles.find((a) => a.id === selectedId),
    [articles, selectedId]
  );

  const selectedTasks = useMemo(() => {
    if (!selectedArticle?.content) return [] as string[];
    return selectedArticle.content
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => /^\d+\)/.test(line))
      .map((line) => line.replace(/^\d+\)\s*/, ''));
  }, [selectedArticle?.content]);

  const selectedRoute = useMemo(() => {
    if (!selectedArticle?.content) return '';
    const match = selectedArticle.content.match(/\/[a-z0-9\-\/]+/i);
    if (!match) return '';
    return match[0].replace(/[),.]+$/, '');
  }, [selectedArticle?.content]);

  useEffect(() => {
    if (!selectedArticle) return;
    const group = groupedArticles.find((entry) => entry.articles.some((item) => item.id === selectedArticle.id));
    if (!group) return;
    setOpenGroups((prev) => ({
      ...prev,
      [group.id]: true,
    }));
  }, [groupedArticles, selectedArticle]);

  useEffect(() => {
    if (!selectedArticle?.slug || typeof window === 'undefined') {
      const canonicalBase = typeof window !== 'undefined' ? window.location.origin : 'https://explanatum.co.uk';
      updateMetaTags({
        title: 'Explanatum Help Center',
        description: 'Guides, walkthroughs, and troubleshooting for every Explanatum module.',
        canonicalUrl: `${canonicalBase}/help`,
        ogTitle: 'Explanatum Help Center',
        ogDescription: 'Guides, walkthroughs, and troubleshooting for every Explanatum module.',
        ogUrl: `${canonicalBase}/help`,
        ogImage: DEFAULT_OG_IMAGE,
        ogType: 'website',
        twitterTitle: 'Explanatum Help Center',
        twitterDescription: 'Guides, walkthroughs, and troubleshooting for every Explanatum module.',
        twitterUrl: `${canonicalBase}/help`,
        twitterImage: DEFAULT_OG_IMAGE,
        twitterCard: 'summary_large_image',
        robots: 'index,follow',
      });
      return;
    }
    const description = selectedArticle.content
      ? selectedArticle.content
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line && !line.endsWith(':'))
          .slice(1, 4)
          .join(' ')
      : 'Practical help guide from Explanatum.';
    const canonicalBase = window.location.origin || 'https://explanatum.co.uk';
    updateMetaTags({
      title: `${selectedArticle.title} | Explanatum Help Center`,
      description,
      canonicalUrl: `${canonicalBase}/help/${selectedArticle.slug}`,
      ogTitle: selectedArticle.title,
      ogDescription: description,
      ogUrl: `${canonicalBase}/help/${selectedArticle.slug}`,
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'article',
      twitterTitle: selectedArticle.title,
      twitterDescription: description,
      twitterUrl: `${canonicalBase}/help/${selectedArticle.slug}`,
      twitterImage: DEFAULT_OG_IMAGE,
      twitterCard: 'summary_large_image',
      robots: 'index,follow',
    });
  }, [selectedArticle?.slug, selectedArticle?.title, selectedArticle?.content]);

  useEffect(() => {
    if (!routeSlug && articleSlug) {
      navigate(`/help/${articleSlug}`, { replace: true });
    }
  }, [routeSlug, articleSlug, navigate]);

  useEffect(() => {
    const fetchContent = async () => {
      if (!selectedArticle?.id || selectedArticle.content || !activeOrgId) return;
      try {
        const full = await supportService.getKbArticle({
          id: selectedArticle.id,
          organization_id: activeOrgId,
        });
        setArticles((prev) =>
          prev.map((item) => (item.id === full.id ? { ...item, content: full.content } : item))
        );
      } catch (err: any) {
        console.error('Failed to load article content:', err);
        setError(err?.message || 'Failed to load article content');
      }
    };

    void fetchContent();
  }, [selectedArticle?.id, selectedArticle?.content, activeOrgId]);

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-amber-50">
      <div className="relative">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 right-10 h-56 w-56 rounded-full bg-amber-200/50 blur-3xl" />
          <div className="absolute top-40 left-8 h-64 w-64 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="absolute bottom-20 right-1/3 h-48 w-48 rounded-full bg-rose-200/40 blur-3xl" />
        </div>
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-10 rounded-3xl border border-slate-200 bg-white/90 p-8 shadow-[0_20px_60px_-40px_rgba(15,23,42,0.45)] backdrop-blur">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Explanatum Guides</p>
                <h1 className="mt-3 text-4xl font-black text-slate-900 sm:text-5xl">
                  Help Center
                </h1>
                <p className="mt-3 max-w-2xl text-base text-slate-600">
                  Playbooks, checklists, and module walkthroughs designed for real workflows. Find the exact action you need,
                  fast.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Modules</p>
                  <p className="mt-1 text-lg font-semibold">All hubs</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-700">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Depth</p>
                  <p className="mt-1 text-lg font-semibold">Task guides</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Support</p>
                  <p className="mt-1 text-lg font-semibold">Quick answers</p>
                </div>
              </div>
            </div>
          </div>

        {loadingOrg && !isPublicView ? (
          <div className="rounded-2xl bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
            Loading your workspace...
          </div>
        ) : !activeOrgId && !isPublicView ? (
          <div className="rounded-2xl bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
            Select or create an organization to view help articles.
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[300px,1fr]">
            <aside className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-[0_20px_50px_-35px_rgba(15,23,42,0.35)] backdrop-blur">
              <div className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Articles</div>
              {loadingArticles ? (
                <div className="text-sm text-gray-500">Loading articles...</div>
              ) : publishedArticles.length === 0 ? (
                <div className="text-sm text-gray-500">No published articles yet.</div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
                      <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search articles"
                        className="w-full rounded-xl border border-slate-200 bg-white px-9 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      />
                    </div>
                    <select
                      value={activeGroupId}
                      onChange={(event) => setActiveGroupId(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    >
                      <option value="all">All modules</option>
                      {moduleGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.title}
                        </option>
                      ))}
                      {groupedArticles.some((group) => group.id === 'other') ? (
                        <option value="other">Other</option>
                      ) : null}
                    </select>
                  </div>
                  {filteredGroups.map((group) => {
                    const isOpen = openGroups[group.id] ?? true;
                    return (
                      <div key={group.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenGroups((prev) => ({
                              ...prev,
                              [group.id]: !isOpen,
                            }))
                          }
                          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
                        >
                          <span>{group.title}</span>
                          <svg
                            className={`h-4 w-4 text-slate-400 transition ${isOpen ? 'rotate-180' : ''}`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        {isOpen && (
                          <div className="space-y-1 px-3 pb-3">
                            {group.articles.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-gray-400">No articles yet.</div>
                            ) : (
                              group.articles.map((article) => (
                                <button
                                  key={article.id}
                                  onClick={() => {
                                    setSelectedId(article.id);
                                    if (article.slug) {
                                      navigate(`/help/${article.slug}`);
                                    }
                                  }}
                                  className={`w-full rounded-xl px-3 py-2 text-left text-sm font-medium transition ${
                                    selectedId === article.id
                                      ? 'bg-slate-900 text-white'
                                      : 'text-slate-700 hover:bg-slate-100'
                                  }`}
                                >
                                  {article.title}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </aside>

            <section className="rounded-3xl border border-slate-200 bg-white/90 p-8 shadow-[0_30px_70px_-45px_rgba(15,23,42,0.4)] backdrop-blur">
              {error ? (
                <div className="text-sm text-red-600">{error}</div>
              ) : !selectedArticle ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500">
                  Select an article to view it.
                </div>
              ) : (
                <div>
                  <div className="border-b border-gray-100 pb-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Guide</p>
                    <h2 className="mt-2 text-3xl font-bold text-slate-900">{selectedArticle.title}</h2>
                    <p className="mt-2 text-xs text-slate-500">Updated {selectedArticle.updated_at?.slice(0, 10) || 'recently'}</p>
                  </div>
                  <div className="mt-6 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                    {selectedArticle.content || 'Loading article...'}
                  </div>
                  <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                    <h3 className="text-base font-semibold text-slate-900">Tasks in this guide</h3>
                    {selectedTasks.length === 0 ? (
                      <p className="mt-2 text-sm text-slate-500">No task steps listed for this article yet.</p>
                    ) : (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                        {selectedTasks.map((task) => (
                          <li key={task}>{task}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-base font-semibold text-slate-900">FAQ & Troubleshooting</h3>
                    <div className="mt-3 space-y-3 text-sm text-slate-700">
                      <div>
                        <p className="font-semibold text-gray-900">Where do I open this module?</p>
                        <p>
                          {selectedRoute
                            ? `Go to ${selectedRoute}.`
                            : 'Open it from the main module navigation.'}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Why is my list empty?</p>
                        <p>Most modules start empty. Create your first record in the primary tab and refresh.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Tabs are not switching.</p>
                        <p>Use the tab bar. If the module uses URL hashes, verify the URL includes the correct #tab.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </section>
    </div>
    </main>
  );
};

export default HelpCenter;
